import React, { useEffect, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { fetchMarketChanges, markMarketChangesSeen } from '@/lib/marketExperienceApi';
import { isNoOpMarketChange, marketChangeDisplayDetail, marketChangeDisplayTitle, mktLabel } from '@/lib/marketExperience';
import { useTranslation } from '@/components/utils/translations';

function changeRows(payload) {
  if (!payload) return [];
  if (Array.isArray(payload)) return payload;
  return payload.changes || payload.rows || payload.items || payload.data || [];
}

function visibleChangeRows(payload) {
  return changeRows(payload).filter((row) => !isNoOpMarketChange(row));
}

export default function MarketChanges({ tenantId, lens, countryFilter = 'all' }) {
  const { t } = useTranslation();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const country = countryFilter === 'all' ? undefined : countryFilter;

  useEffect(() => {
    let cancelled = false;
    if (!tenantId || !lens) return;
    setLoading(true);
    fetchMarketChanges(tenantId, lens, country)
      .then((data) => {
        if (!cancelled) setRows(visibleChangeRows(data));
      })
      .catch(() => {
        if (!cancelled) setRows([]);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => { cancelled = true; };
  }, [tenantId, lens, country]);

  const unread = rows.filter((r) => r.seen !== true && r.is_read !== true);
  const ids = unread.map((r) => r.id).filter(Boolean);

  const markSeen = async () => {
    if (!ids.length) return;
    try {
      await markMarketChangesSeen(tenantId, ids, lens);
      setRows((prev) => prev.map((r) => (ids.includes(r.id) ? { ...r, seen: true } : r)));
    } catch {
      /* keep list */
    }
  };

  return (
    <Card className="border-0 shadow-md">
      <CardHeader className="flex flex-row items-center justify-between space-y-0">
        <CardTitle className="text-base text-[#0A1A2F]">{t('mktWhatChanged')}</CardTitle>
        {ids.length > 0 && (
          <Button variant="ghost" size="sm" className="text-xs text-[#00C2C2]" onClick={markSeen}>
            {t('mktMarkRead')}
          </Button>
        )}
      </CardHeader>
      <CardContent>
        {loading ? (
          <div className="h-20 bg-gray-100 rounded-md animate-pulse" />
        ) : rows.length === 0 ? (
          <p className="text-sm text-[#5C6B7A]">{t('mktNoRecentChanges')}</p>
        ) : (
          <ul className="space-y-3">
            {rows.slice(0, 8).map((row, i) => {
              const title = marketChangeDisplayTitle(row);
              const detail = marketChangeDisplayDetail(row);
              return (
              <li key={row.id || i} className="text-sm border-b border-gray-100 pb-2 last:border-0">
                <p className="text-[#0A1A2F]">{mktLabel(t, title)}</p>
                {detail && detail !== title ? (
                  <p className="text-xs text-[#5C6B7A] mt-0.5">{detail}</p>
                ) : null}
              </li>
              );
            })}
          </ul>
        )}
      </CardContent>
    </Card>
  );
}
